<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';

const route = useRoute();
const store = useStore();
const invoice = ref(null);
const isLoading = ref(true);

const formatNumber = (value) => {
	if (value == null || Number.isNaN(Number(value))) return '-';
	return Number(value).toLocaleString('vi-VN');
};

const formatDateTime = (value, time) => {
	if (!value) return '-';
	const date = dayjs(value).format('DD/MM/YYYY');
	return time ? `${date} ${time}` : date;
};

const formatPrice = (price) => {
	if (price == null || Number.isNaN(Number(price))) return '';
	return Number(price).toLocaleString('vi-VN');
};

const getInvoice = async () => {
	try {
		isLoading.value = true;
		const response = await fetch(
			process.env.VUE_APP_BASE_URL +
				`v1/leader/invoices/${route.params.id}`,
			{
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					'x-access-token': route?.query?.token,
					'x-subdomain': route?.query?.subdomain,
				},
			}
		);
		const result = await response.json();
		console.log(result);
		invoice.value = result.data || null;
	} catch (err) {
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		isLoading.value = false;
		if (route?.query?.is_print === 'true') {
			setTimeout(() => {
				window.print();
			}, 3000);
		}
	}
};

onMounted(() => {
	getInvoice();
});
</script>

<template>
	<div class="invoice-pdf-page">
		<!-- <div v-if="isLoading" class="invoice-loading">Đang tải hoá đơn...</div>
		<div v-else-if="!invoice" class="invoice-loading">
			Không tìm thấy dữ liệu hoá đơn.
		</div> -->
		<div id="package_invoice_pdf" class="invoice-sheet">
			<div class="flex items-center justify-between">
				<div class="invoice-brand">
					<img
						src="@/assets/images/logo/luca.svg"
						alt="luca-education"
						title="Luca Education"
						class="logo"
						height="52"
						width="68"
					/>
				</div>
				<div class="text-neural-700 text-xs">
					<div>SỐ HÓA ĐƠN: {{ invoice?.id }}</div>
					<div>
						NGÀY TẠO: {{ formatDateTime(invoice?.inserted_at) }}
					</div>
				</div>
			</div>
			<h1
				class="
					text-neutral-700 text-3xl
					font-semibold
					text-center
					mt-4
					mb-8
				"
			>
				HOÁ ĐƠN DỊCH VỤ
			</h1>
			<section class="invoice-info-grid text-neutral-700 text-xs">
				<div class="info-cell info-cell--tl">
					<div class="font-semibold mb-1">Thông tin khách hàng</div>
					<div>Tên cơ sở: {{ invoice?.center?.name }}</div>
					<div>
						Người tạo: {{ invoice?.created_by_staff?.full_name }}
					</div>
				</div>
				<div
					class="info-cell info-cell--tr"
					v-if="invoice?.type === 'contract_package'"
				>
					<div class="font-semibold mb-1">Gói dịch vụ</div>
					<div>
						Gói đăng ký: {{ invoice?.package?.duration }} tháng
					</div>
					<div>
						Ngày bắt đầu:
						{{ formatDateTime(invoice?.apply_date) }} 00:00
					</div>
					<div>
						Ngày kết thúc:
						{{ formatDateTime(invoice?.end_date) }} 23:59
					</div>
				</div>
				<div class="info-cell info-cell--tr" v-else>
					<div class="font-semibold mb-1">Gói tiện ích</div>
					<div>
						Gói dung lượng:
						{{ invoice?.title }}
					</div>
					<div>
						Đơn giá:
						{{ formatPrice(invoice?.price) }} VNĐ
					</div>
				</div>
				<div
					class="info-cell info-cell--bl"
					v-if="invoice?.type === 'contract_package'"
				>
					<div class="font-semibold mb-1">Số lượng học sinh</div>
					<div>Học sinh đăng ký: {{ invoice?.seats }}</div>
					<div>Học sinh được tặng: {{ invoice?.bonus_seats }}</div>
					<div>Tổng sử dụng: {{ invoice?.total_seats }}</div>
				</div>
				<div
					class="info-cell info-cell--br"
					v-if="invoice?.type === 'contract_package'"
				>
					<div class="font-semibold mb-1">Bảng giá</div>
					<div>Chi phí: 68.000/tháng/học sinh</div>
					<div>
						Gói {{ invoice?.package?.duration }} tháng:
						{{ invoice?.student_price?.amount }}/{{
							invoice?.package?.duration
						}}
						tháng/học sinh
					</div>
				</div>
			</section>

			<section class="invoice-table-wrap">
				<table class="invoice-table">
					<thead>
						<tr class="text-neutral-700 text-xs font-semibold">
							<th class="w-10 text-center">STT</th>
							<th class="text-left">Tên dịch vụ</th>
							<th class="text-left">Đơn giá (VNĐ)</th>
							<th class="text-left">Số lượng</th>
							<th class="text-right">Thành tiền (VNĐ)</th>
						</tr>
					</thead>
					<tbody>
						<tr class="text-neutral-700 text-xs">
							<td class="text-center">1</td>
							<td
								class="text-left"
								v-if="invoice?.type === 'contract_package'"
							>
								Gói {{ invoice?.package?.duration }} tháng
							</td>
							<td class="text-left" v-else>
								{{ invoice?.title }}
							</td>
							<td
								class="text-left"
								v-if="invoice?.type === 'contract_package'"
							>
								{{ invoice?.student_price?.amount }}
							</td>
							<td class="text-left" v-else>
								{{ formatPrice(invoice?.price) }}
							</td>
							<td
								class="text-left"
								v-if="invoice?.type === 'contract_package'"
							>
								{{ invoice?.seats }}
							</td>
							<td class="text-left" v-else>
								{{ invoice?.quantity || 1 }}
							</td>
							<td
								class="text-right"
								v-if="invoice?.type === 'contract_package'"
							>
								₫{{ invoice?.price_before_discount?.amount }}
							</td>
							<td class="text-right" v-else>
								{{
									formatPrice(invoice?.total_price) ||
									formatPrice(invoice?.price)
								}}
							</td>
						</tr>
						<tr
							class="
								invoice-table__summary
								text-neutral-700 text-xs
							"
						>
							<td colspan="4" class="text-left">Tổng (VNĐ)</td>
							<td
								class="text-right"
								v-if="invoice?.type === 'contract_package'"
							>
								{{ invoice?.price_before_discount?.amount }}
							</td>
							<td class="text-right" v-else>
								{{
									formatPrice(invoice?.total_price) ||
									formatPrice(invoice?.price)
								}}
							</td>
						</tr>
						<tr
							class="
								invoice-table__summary
								text-neutral-700 text-xs
							"
							v-if="
								invoice?.large_center_discount?.amount
									?.amount_in_cents > 0
							"
						>
							<td colspan="4" class="text-left">
								Ưu đãi trung tâm lớn({{
									invoice?.large_center_discount?.percent
								}}%)
							</td>
							<td class="text-right">
								-{{
									invoice?.large_center_discount?.amount
										?.amount
								}}
							</td>
						</tr>
						<tr
							class="
								invoice-table__summary
								text-neutral-700 text-xs
							"
							v-if="
								invoice?.lan_toa_discount?.amount_in_cents > 0
							"
						>
							<td colspan="4" class="text-left">
								Ưu đãi "Lan toả Luca"
							</td>
							<td class="text-right">
								-{{ invoice?.lan_toa_discount?.amount }}
							</td>
						</tr>
						<tr
							class="
								invoice-table__total
								text-neutral-700 text-xs
							"
						>
							<td colspan="4" class="text-left">Thành tiền</td>
							<td
								class="text-right font-semibold"
								v-if="invoice?.type === 'contract_package'"
							>
								{{ invoice?.price?.amount }} VNĐ
							</td>
							<td class="text-right font-semibold" v-else>
								{{
									formatPrice(invoice?.total_price) ||
									formatPrice(invoice?.price)
								}}
								VNĐ
							</td>
						</tr>
					</tbody>
				</table>
			</section>
			<p class="text-neutral-700 text-xs mt-4">
				Đây chỉ là hóa đơn dịch vụ. Hóa đơn đỏ xuất cho công ty sẽ được
				gửi vào email bạn đã cung cấp ngay sau khi chúng tôi nhận được
				khoản thanh toán
			</p>

			<footer
				class="
					flex
					items-center
					justify-between
					mt-10
					text-neutral-700 text-ls
				"
			>
				<div>
					<div class="font-semibold">
						Công ty cổ phần Luca Education
					</div>
					<div>
						Số 23A, ngõ 178 Giải Phóng, phường Phương Liệt, TP. Hà
						Nội, Việt Nam
					</div>
				</div>
				<div>
					<div class="font-semibold">MST: 0109767117</div>
					<div>Email: hello@luca.education</div>
				</div>
			</footer>
		</div>
	</div>
</template>

<style scoped>
.invoice-pdf-page {
	min-height: 100vh;
	background: #eef2f6;
	padding: 24px;
	display: flex;
	justify-content: center;
	align-items: flex-start;
}

.invoice-sheet {
	width: 794px;
	max-width: 100%;
	background: #fff;
	padding: 36px 40px 28px;
	color: #334155;
	font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.invoice-brand {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.invoice-info-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	border: 1px solid #cbd5e1;
	margin-bottom: 20px;
}

.info-cell {
	padding: 14px 16px;
	font-size: 13px;
	line-height: 1.55;
}

.info-cell--tl,
.info-cell--bl {
	border-right: 1px solid #e2e8f0;
}

.info-cell--tl,
.info-cell--tr {
	border-bottom: 1px solid #e2e8f0;
}

.invoice-table-wrap {
	border: 1px solid #cbd5e1;
	overflow: hidden;
	margin-bottom: 20px;
}

.invoice-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}

.invoice-table thead {
	background: #eef2f7;
}

.invoice-table th,
.invoice-table td {
	padding: 12px 14px;
	border-bottom: 1px solid #e2e8f0;
	border-right: 1px solid #e2e8f0;
	color: #334155;
}

.invoice-table th:last-child,
.invoice-table td:last-child {
	border-right: none;
}

.invoice-table tbody tr:last-child th,
.invoice-table tbody tr:last-child td {
	border-bottom: none;
}

.invoice-table th {
	font-weight: 700;
	text-align: left;
}

.invoice-table__summary {
	background: #eef2f7;
}

.invoice-table__summary td,
.invoice-table__total td {
	border-right: none;
}

@media print {
	.invoice-pdf-page {
		background: #fff;
		padding: 0;
		min-height: auto;
	}

	.invoice-sheet {
		width: 100%;
		max-width: none;
		box-shadow: none;
		padding: 16px 20px;
	}
}

/* @page {
	size: A4;
	margin: 12mm;
} */
</style>
